import axios from "axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchError } from "./commonSlice";

const initialState = {
  orders: {
    list: [],
    perPage: 10,
    total: 0,
    orderCount: 0,
    loading: false,
  },
  orderHistory: {
    list: [],
    perPage: 10,
    total: 0,
    loading: false,
  },
  returns: {
    list: [],
    perPage: 10,
    total: 0,
    orderCount: 0,
    loading: false,
  },
  payments: {
    list: [],
    perPage: 10,
    total: 0,
    loading: false,
  },
  productReport: {
    list: [],
    perPage: 10,
    total: 0,
    loading: false,
  },
  statusList: {
    list: [],
    perPage: 10,
    total: 0,
    loading: false,
  },
  marketing: {
    list: [],
    perPage: 10,
    total: 0,
    loading: false,
  },
  marketingHistory: {
    list: [],
    perPage: 10,
    total: 0,
    loading: false,
  },
  returnReport: {
    list: [],
    perPage: 20,
    total: 0,
    loading: false,
  },
  returnReasonAnalysis: {
    summary: null,
    reasonBreakdown: [],
    skuBreakdown: [],
    statusBreakdown: [],
    loading: false,
  },
  returnReportSkuList: {
    skus: [],
    loading: false,
  },
};

// Import Orders (CSV)
export const importMyntraOrders = createAsyncThunk(
  "myntra/import",
  async (formData, { dispatch }) => {
    try {
      const response = await axios.post("myntra/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

// Import Payments (Excel)
export const importMyntraPayments = createAsyncThunk(
  "myntra/payment/import",
  async (formData, { dispatch }) => {
    try {
      const response = await axios.post("myntra/payment/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

// Get Import History List
export const getMyntraOrderHistoryList = createAsyncThunk(
  "myntra/order-history-list",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("myntra/order/import-history/list", {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

// Get Orders List
export const getMyntraOrderList = createAsyncThunk(
  "myntra/order-list",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("myntra/orders", { params: data });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

// Delete Order
export const deleteMyntraOrder = createAsyncThunk(
  "myntra/delete-order",
  async (id, { dispatch }) => {
    try {
      const response = await axios.delete(`myntra/orders/${id}`);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

// Get Returns List
export const getMyntraReturnList = createAsyncThunk(
  "myntra/return-list",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("myntra/returns", { params: data });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

// Delete Return
export const deleteMyntraReturn = createAsyncThunk(
  "myntra/delete-return",
  async (id, { dispatch }) => {
    try {
      const response = await axios.delete(`myntra/orders/${id}`);
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

// Get Payments List
export const getMyntraPaymentList = createAsyncThunk(
  "myntra/payment-list",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("myntra/payment/list", { params: data });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

// Get Product Report
export const getMyntraProductReport = createAsyncThunk(
  "myntra/product-report",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("myntra/product-report", {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

// Get Statistics
export const getMyntraStatistics = createAsyncThunk(
  "myntra/statistics",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("myntra/statistics", { params: data });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

// Get Statistics Status List
export const getMyntraStatisticsStatusList = createAsyncThunk(
  "myntra/statistics-status-list",
  async ({ id, data }, { dispatch }) => {
    try {
      const response = await axios.get(`myntra/${id}/product`, {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

// Get Finance Dashboard
export const getMyntraFinanceDashboard = createAsyncThunk(
  "myntra/finance-dashboard",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("myntra/finance-dashboard", {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

// Import Marketing CSV
export const importMyntraMarketing = createAsyncThunk(
  "myntra/marketing/import",
  async (formData, { dispatch }) => {
    try {
      const response = await axios.post("myntra/marketing/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

// Get Marketing List
export const getMyntraMarketingList = createAsyncThunk(
  "myntra/marketing-list",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("myntra/marketing/list", {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

// Get Marketing Import History List
export const getMyntraMarketingHistoryList = createAsyncThunk(
  "myntra/marketing-history-list",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("myntra/marketing/import-history/list", {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  },
);

// Import Return Report CSV
export const importMyntraReturnReport = createAsyncThunk(
  "myntra/return-report/import",
  async (formData, { dispatch }) => {
    try {
      const response = await axios.post(
        "myntra/return-report/import",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// Get Return Report List
export const getMyntraReturnReportList = createAsyncThunk(
  "myntra/return-report/list",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("myntra/return-report/list", {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// Get Return Reason Analysis
export const getMyntraReturnReasonAnalysis = createAsyncThunk(
  "myntra/return-report/analysis",
  async (data, { dispatch }) => {
    try {
      const response = await axios.get("myntra/return-report/analysis", {
        params: data,
      });
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

// Get Return Report SKU list
export const getMyntraReturnReportSkuList = createAsyncThunk(
  "myntra/return-report/sku-list",
  async (_, { dispatch }) => {
    try {
      const response = await axios.get("myntra/return-report/sku-list");
      return response;
    } catch (error) {
      dispatch(fetchError(error));
      return error;
    }
  }
);

const myntraSlice = createSlice({
  name: "myntra",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Orders List
      .addCase(getMyntraOrderList.pending, (state) => {
        state.orders.loading = true;
      })
      .addCase(getMyntraOrderList.fulfilled, (state, action) => {
        state.orders.loading = false;
        state.orders.list = action?.payload?.data?.data?.orders ?? [];
        state.orders.perPage = action?.payload?.data?.data?.perPage ?? 10;
        state.orders.total = action?.payload?.data?.data?.total ?? 0;
        state.orders.orderCount = action?.payload?.data?.data?.orderCount ?? 0;
      })
      .addCase(getMyntraOrderList.rejected, (state) => {
        state.orders.loading = false;
        state.orders.list = [];
        state.orders.perPage = 10;
        state.orders.total = 0;
        state.orders.orderCount = 0;
      })

      // Order History List
      .addCase(getMyntraOrderHistoryList.pending, (state) => {
        state.orderHistory.loading = true;
      })
      .addCase(getMyntraOrderHistoryList.fulfilled, (state, action) => {
        state.orderHistory.loading = false;
        state.orderHistory.list =
          action?.payload?.data?.data?.importHistory ?? [];
        state.orderHistory.perPage = action?.payload?.data?.data?.perPage ?? 10;
        state.orderHistory.total = action?.payload?.data?.data?.total ?? 0;
      })
      .addCase(getMyntraOrderHistoryList.rejected, (state) => {
        state.orderHistory.loading = false;
        state.orderHistory.list = [];
        state.orderHistory.perPage = 10;
        state.orderHistory.total = 0;
      })

      // Returns List
      .addCase(getMyntraReturnList.pending, (state) => {
        state.returns.loading = true;
      })
      .addCase(getMyntraReturnList.fulfilled, (state, action) => {
        state.returns.loading = false;
        state.returns.list = action?.payload?.data?.data?.returnList ?? [];
        state.returns.perPage = action?.payload?.data?.data?.perPage ?? 10;
        state.returns.total = action?.payload?.data?.data?.total ?? 0;
        state.returns.orderCount = action?.payload?.data?.data?.orderCount ?? 0;
      })
      .addCase(getMyntraReturnList.rejected, (state) => {
        state.returns.loading = false;
        state.returns.list = [];
        state.returns.perPage = 10;
        state.returns.total = 0;
        state.returns.orderCount = 0;
      })

      // Payments List
      .addCase(getMyntraPaymentList.pending, (state) => {
        state.payments.loading = true;
      })
      .addCase(getMyntraPaymentList.fulfilled, (state, action) => {
        state.payments.loading = false;
        state.payments.list = action?.payload?.data?.data?.payments ?? [];
        state.payments.perPage = action?.payload?.data?.data?.perPage ?? 10;
        state.payments.total = action?.payload?.data?.data?.total ?? 0;
      })
      .addCase(getMyntraPaymentList.rejected, (state) => {
        state.payments.loading = false;
        state.payments.list = [];
        state.payments.perPage = 10;
        state.payments.total = 0;
      })

      // Product Report
      .addCase(getMyntraProductReport.pending, (state) => {
        state.productReport.loading = true;
      })
      .addCase(getMyntraProductReport.fulfilled, (state, action) => {
        state.productReport.loading = false;
        state.productReport.list =
          action?.payload?.data?.data?.paginatedList ?? [];
        state.productReport.perPage =
          action?.payload?.data?.data?.perPage ?? 10;
        state.productReport.total = action?.payload?.data?.data?.total ?? 0;
      })
      .addCase(getMyntraProductReport.rejected, (state) => {
        state.productReport.loading = false;
        state.productReport.list = [];
        state.productReport.perPage = 10;
        state.productReport.total = 0;
      })

      // Statistics Status List
      .addCase(getMyntraStatisticsStatusList.pending, (state) => {
        state.statusList.loading = true;
      })
      .addCase(getMyntraStatisticsStatusList.fulfilled, (state, action) => {
        state.statusList.loading = false;
        state.statusList.list = action?.payload?.data?.data?.products ?? [];
        state.statusList.perPage = action?.payload?.data?.data?.perPage ?? 10;
        state.statusList.total = action?.payload?.data?.data?.totalProduct ?? 0;
      })
      .addCase(getMyntraStatisticsStatusList.rejected, (state) => {
        state.statusList.loading = false;
        state.statusList.list = [];
        state.statusList.perPage = 10;
        state.statusList.total = 0;
      })

      // Marketing List
      .addCase(getMyntraMarketingList.pending, (state) => {
        state.marketing.loading = true;
      })
      .addCase(getMyntraMarketingList.fulfilled, (state, action) => {
        state.marketing.loading = false;
        state.marketing.list = action?.payload?.data?.data?.list ?? [];
        state.marketing.perPage = action?.payload?.data?.data?.perPage ?? 10;
        state.marketing.total = action?.payload?.data?.data?.total ?? 0;
      })
      .addCase(getMyntraMarketingList.rejected, (state) => {
        state.marketing.loading = false;
        state.marketing.list = [];
        state.marketing.perPage = 10;
        state.marketing.total = 0;
      })

      // Marketing Import History
      .addCase(getMyntraMarketingHistoryList.pending, (state) => {
        state.marketingHistory.loading = true;
      })
      .addCase(getMyntraMarketingHistoryList.fulfilled, (state, action) => {
        state.marketingHistory.loading = false;
        state.marketingHistory.list = action?.payload?.data?.data?.list ?? [];
        state.marketingHistory.perPage =
          action?.payload?.data?.data?.perPage ?? 10;
        state.marketingHistory.total = action?.payload?.data?.data?.total ?? 0;
      })
      .addCase(getMyntraMarketingHistoryList.rejected, (state) => {
        state.marketingHistory.loading = false;
        state.marketingHistory.list = [];
        state.marketingHistory.perPage = 10;
        state.marketingHistory.total = 0;
      })

      // Return Report List
      .addCase(getMyntraReturnReportList.pending, (state) => {
        state.returnReport.loading = true;
      })
      .addCase(getMyntraReturnReportList.fulfilled, (state, action) => {
        state.returnReport.loading = false;
        state.returnReport.list = action?.payload?.data?.data?.list ?? [];
        state.returnReport.perPage = action?.payload?.data?.data?.perPage ?? 20;
        state.returnReport.total = action?.payload?.data?.data?.total ?? 0;
      })
      .addCase(getMyntraReturnReportList.rejected, (state) => {
        state.returnReport.loading = false;
        state.returnReport.list = [];
        state.returnReport.total = 0;
      })

      // Return Reason Analysis
      .addCase(getMyntraReturnReasonAnalysis.pending, (state) => {
        state.returnReasonAnalysis.loading = true;
      })
      .addCase(getMyntraReturnReasonAnalysis.fulfilled, (state, action) => {
        const d = action?.payload?.data?.data;
        state.returnReasonAnalysis.loading = false;
        state.returnReasonAnalysis.summary = d?.summary ?? null;
        state.returnReasonAnalysis.reasonBreakdown = d?.reasonBreakdown ?? [];
        state.returnReasonAnalysis.skuBreakdown = d?.skuBreakdown ?? [];
        state.returnReasonAnalysis.statusBreakdown = d?.statusBreakdown ?? [];
      })
      .addCase(getMyntraReturnReasonAnalysis.rejected, (state) => {
        state.returnReasonAnalysis.loading = false;
        state.returnReasonAnalysis.summary = null;
        state.returnReasonAnalysis.reasonBreakdown = [];
        state.returnReasonAnalysis.skuBreakdown = [];
        state.returnReasonAnalysis.statusBreakdown = [];
      })

      // Return Report SKU List
      .addCase(getMyntraReturnReportSkuList.pending, (state) => {
        state.returnReportSkuList.loading = true;
      })
      .addCase(getMyntraReturnReportSkuList.fulfilled, (state, action) => {
        state.returnReportSkuList.loading = false;
        state.returnReportSkuList.skus =
          action?.payload?.data?.data?.skus ?? [];
      })
      .addCase(getMyntraReturnReportSkuList.rejected, (state) => {
        state.returnReportSkuList.loading = false;
        state.returnReportSkuList.skus = [];
      });
  },
});

export default myntraSlice.reducer;
